/**
 * WasmLogger - WASM/Rust Typesetter Operations (v2 Taxonomy)
 *
 * Taxonomy: wasm.{operation}[.{flavor}][.{sub-flavor}]
 *
 * Operations:
 * - init: WASM module initialization
 * - interop: JS <-> WASM boundary calls
 * - shaping: HarfBuzz text shaping
 * - breaking: Line breaking algorithm
 * - layout: Layout calculation
 * - paginate: Pagination operations
 * - snapshot: Snapshot cache operations
 * - incremental: Incremental text updates
 * - delete: Delete operation diagnostics
 * - memory: Memory diagnostics
 *
 * @example
 * WasmLogger.init.complete('1.0.0', { features: ['shaping'] });
 * WasmLogger.paginate.start('doc-123');
 * WasmLogger.paginate.complete(15.2, { pageCount: 3 });
 * WasmLogger.delete.diag.before({ position: 42, deleteCount: 1 });
 */

import { WeBelieveLogger, captureCallerSource, captureFullStack } from '../WeBelieveLogger';
import { WasmStateCapture, createStateAccessor, type TypesetterStateAccessor, type WasmStateSnapshot } from './WasmStateSnapshot';

export const WasmLogger = {
  // ============================================================================
  // WASM Initialization
  // ============================================================================
  init: {
    /**
     * Log WASM initialization started
     */
    start: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'wasm.init.start', 'WASM initialization started', source, data);
    },

    /**
     * Log WASM initialization completed
     */
    complete: (version: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'wasm.init.complete', `WASM initialized: v${version}`, source, { version, ...data });
    },

    /**
     * Log WASM initialization failed
     * Uses full stack capture for error-level logs
     */
    fail: (error: Error, data?: Record<string, unknown>) => {
      const source = captureFullStack();
      WeBelieveLogger.logWithSource('error', 'wasm.init.fail', error.message, source, {
        ...data,
        errorStack: error.stack,
      });
    },
  },

  // ============================================================================
  // JS <-> WASM Interop
  // ============================================================================
  interop: {
    /**
     * Log JS -> WASM call
     */
    call: (operation: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.interop.call', `Calling WASM: ${operation}`, source, { operation, ...data });
    },

    /**
     * Log WASM -> JS result
     */
    result: (operation: string, durationMs: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.performanceWithSource('wasm.interop.result', `WASM result: ${operation}`, durationMs, source, { operation, ...data });
    },
  },

  // ============================================================================
  // Text Shaping (HarfBuzz)
  // ============================================================================
  shaping: {
    /**
     * Log text shaping started
     */
    start: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.shaping.start', 'Text shaping started', source, data);
    },

    /**
     * Log text shaping completed
     */
    complete: (durationMs: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.performanceWithSource('wasm.shaping.complete', 'Text shaping completed', durationMs, source, data);
    },

    /**
     * Log text shaping error
     * Uses full stack capture for error-level logs
     */
    fail: (error: Error, data?: Record<string, unknown>) => {
      const source = captureFullStack();
      WeBelieveLogger.logWithSource('error', 'wasm.shaping.fail', error.message, source, {
        ...data,
        errorStack: error.stack,
      });
    },
  },

  // ============================================================================
  // Line Breaking
  // ============================================================================
  breaking: {
    /**
     * Log line breaking started
     */
    start: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.breaking.start', 'Line breaking started', source, data);
    },

    /**
     * Log line breaking completed
     */
    complete: (durationMs: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.performanceWithSource('wasm.breaking.complete', 'Line breaking completed', durationMs, source, data);
    },
  },

  // ============================================================================
  // Layout Calculation
  // ============================================================================
  layout: {
    /**
     * Log layout started
     */
    start: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'wasm.layout.start', 'Layout calculation started', source, data);
    },

    /**
     * Log layout completed
     */
    complete: (durationMs: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.performanceWithSource('wasm.layout.complete', 'Layout calculation completed', durationMs, source, data);
    },

    /**
     * Log layout error
     * Uses full stack capture for error-level logs
     */
    fail: (error: Error, data?: Record<string, unknown>) => {
      const source = captureFullStack();
      WeBelieveLogger.logWithSource('error', 'wasm.layout.fail', error.message, source, {
        ...data,
        errorStack: error.stack,
      });
    },

    /**
     * Log dataflow: AST to text runs
     */
    dataflowAstToRuns: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.layout.dataflow.ast-to-runs', 'Converting AST to text runs', source, data);
    },

    /**
     * Log dataflow: text runs to lines
     */
    dataflowRunsToLines: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.layout.dataflow.runs-to-lines', 'Converting text runs to lines', source, data);
    },

    /**
     * Log page format result
     */
    pageFormat: (data: Record<string, unknown>) => {
      const source = captureCallerSource();
      const format = data.format as string;
      const level = format === 'NEW_WITH_LINES' ? 'debug' : 'warn';
      WeBelieveLogger.logWithSource(level, 'wasm.layout.diag.page-format', `Page format: ${format}`, source, data);
    },

    /**
     * Log WASM diagnostics count
     */
    diagWasm: (data: Record<string, unknown>) => {
      const source = captureCallerSource();
      const count = data.diagnosticsCount as number;
      const level = count > 0 ? 'info' : 'debug';
      WeBelieveLogger.logWithSource(level, 'wasm.layout.diag', `WASM diagnostics: ${count} events`, source, data);
    },

    /**
     * Log pagination input (paragraph routing)
     */
    paginationInput: (data: Record<string, unknown>) => {
      const source = captureCallerSource();
      const outputParagraphCount = data.outputParagraphCount as number;
      const potentialParagraphCount = data.potentialParagraphCount as number;
      const level = outputParagraphCount < potentialParagraphCount ? 'warn' : 'debug';
      WeBelieveLogger.logWithSource(level, 'wasm.layout.diag.pagination-input',
        `Pagination input: ${outputParagraphCount} paragraph(s) sent, ${potentialParagraphCount} detected in text`,
        source, data);
    },

    /**
     * 🔬 Phase 3.4 DIAGNOSTIC: Log line spacing parameters being sent to WASM
     * This helps diagnose inconsistent line heights for empty paragraphs
     * 🆕 2026-01-20: Added perParagraphOverride and documentDefault for per-paragraph line spacing diagnostics
     */
    lineSpacingParams: (data: {
      fontSizePx: number;
      fontSizeFp: number;
      lineSpacingMul: number;
      expectedLineHeightPx: number;
      expectedLineHeightFp: number;
      paragraphId?: string;
      paragraphIndex?: number;
      paragraphTextLength?: number;
      isEmpty?: boolean;
      /** 🆕 2026-01-20: Per-paragraph line spacing override from AST (undefined = using document default) */
      perParagraphOverride?: number;
      /** 🆕 2026-01-20: Document-level default line spacing for comparison */
      documentDefault?: number;
    }) => {
      const source = captureCallerSource();
      // 🆕 2026-01-20: Show per-paragraph override info in log message
      const overrideInfo = data.perParagraphOverride !== undefined
        ? ` [PER-PARA: ${data.perParagraphOverride}, doc-default: ${data.documentDefault}]`
        : '';
      WeBelieveLogger.logWithSource('info', 'wasm.layout.diag.line-spacing-params',
        `LINE SPACING: font=${data.fontSizePx}px (${data.fontSizeFp}fp), mul=${data.lineSpacingMul}, expected height=${data.expectedLineHeightPx.toFixed(1)}px (${data.expectedLineHeightFp}fp)${data.isEmpty ? ' [EMPTY]' : ''}${overrideInfo}`,
        source, data);
    },

    /**
     * 🔬 Phase 3.4 DIAGNOSTIC: Log line spacing diagnostics received from WASM
     * Shows what values WASM actually used for line height calculations
     */
    lineSpacingResult: (data: {
      paragraphId?: string;
      inputDefaultFontSizeFp: number | null;
      inputLineSpacingMul: number | null;
      resolvedDefaultFontSizeFp: number;
      resolvedLineSpacingMul: number;
      lineCount: number;
      lineDiagnostics: Array<{
        lineIndex: number;
        isEmpty: boolean;
        heightFp: number;
        ascentFp: number;
        descentFp: number;
        graphemeRange: string;
        heightSource: string;
      }>;
    }) => {
      const source = captureCallerSource();

      // Log summary
      WeBelieveLogger.logWithSource('info', 'wasm.layout.diag.line-spacing-result',
        `LINE SPACING RESULT: input_font=${data.inputDefaultFontSizeFp}fp, input_mul=${data.inputLineSpacingMul}, resolved_font=${data.resolvedDefaultFontSizeFp}fp, resolved_mul=${data.resolvedLineSpacingMul}, lines=${data.lineCount}`,
        source, {
          paragraphId: data.paragraphId?.slice(0, 8),
          inputDefaultFontSizeFp: data.inputDefaultFontSizeFp,
          inputLineSpacingMul: data.inputLineSpacingMul,
          resolvedDefaultFontSizeFp: data.resolvedDefaultFontSizeFp,
          resolvedLineSpacingMul: data.resolvedLineSpacingMul,
          lineCount: data.lineCount,
        });

      // Log per-line details for empty lines
      for (const line of data.lineDiagnostics) {
        if (line.isEmpty) {
          const heightPx = line.heightFp / 64;
          WeBelieveLogger.logWithSource('warn', 'wasm.layout.diag.empty-line-height',
            `EMPTY LINE ${line.lineIndex}: height=${line.heightFp}fp (${heightPx.toFixed(1)}px), range=${line.graphemeRange}, source=${line.heightSource}`,
            source, line);
        }
      }
    },

    /**
     * Log Phase 4 caret routing via HeightIndexManager
     */
    caretRouting: (data: {
      cursorIndex: number;
      graphemeY?: number | null;
      paragraphIndex?: number | null;
      targetPage: number;
      pageChanged: boolean;
      notifiedPages: number[];
      totalRegisteredPages: number;
      heightIndexSize?: number | null;
      totalDocumentHeight?: number | null;
      routingLatencyMs?: number | null;
      binarySearchSteps?: number | null;
      formFeedCrossing?: boolean | null;
      architecture: string;
      phase: string;
    }) => {
      const source = captureCallerSource();
      const { cursorIndex, targetPage, paragraphIndex, notifiedPages } = data;

      const paraInfo = paragraphIndex != null ? `, paragraph ${paragraphIndex}` : '';
      const pagesInfo = notifiedPages.length > 1 ? ` (notified ${notifiedPages.length} pages)` : '';
      const message = `Caret routed to page ${targetPage}${paraInfo} at grapheme ${cursorIndex}${pagesInfo}`;

      WeBelieveLogger.logWithSource('debug', 'wasm.layout.caret-routing', message, source, data);
    },

    /**
     * Log SpillFrontier calculation (Phase 3A)
     */
    spillFrontier: (data: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.layout.step.spill-frontier', 'SpillFrontier calculated', source, data);
    },

    /**
     * Log WASM diagnostics
     */
    wasmDiagnostics: (data: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.layout.diag.wasm', 'WASM diagnostics', source, data);
    },
  },

  // ============================================================================
  // Height Index Manager (Phase 4 - O(log n) Caret Routing)
  // ============================================================================
  heightIndex: {
    /**
     * Log snapshot processing entry for height index updates
     */
    snapshotProcessing: (data: {
      hasHeightIndexManager: boolean;
      snapshotsLength: number;
      snapshotIds: string[];
      snapshotLineCounts: number[];
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.height-index.snapshot-processing',
        `Processing ${data.snapshotsLength} snapshots for height index`,
        source, {
          ...data,
          snapshotIds: data.snapshotIds.map(id => id?.slice(0, 8)),
        });
    },

    /**
     * Log form feed detection during paragraph processing
     */
    formFeedCheck: (data: {
      paragraphId: string;
      graphemeStart: number;
      graphemeEnd: number;
      paragraphTextLength: number;
      paragraphTextPreview: string;
      hasFormFeed: boolean;
      newTextLength: number;
    }) => {
      const source = captureCallerSource();
      const level = data.hasFormFeed ? 'info' : 'debug';
      WeBelieveLogger.logWithSource(level, 'wasm.height-index.form-feed-check',
        `Form feed check: ${data.paragraphId.slice(0, 8)} [${data.graphemeStart}-${data.graphemeEnd}) hasFF=${data.hasFormFeed}`,
        source, {
          ...data,
          paragraphId: data.paragraphId.slice(0, 8),
          paragraphTextPreview: data.paragraphTextPreview.replace(/\f/g, '[FF]').replace(/\n/g, '[NL]'),
        });
    },

    /**
     * Log form feed marked for paragraph
     */
    formFeedMarked: (paragraphId: string, hasFormFeed: boolean) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource(hasFormFeed ? 'info' : 'debug', 'wasm.height-index.form-feed-marked',
        `Form feed ${hasFormFeed ? 'marked' : 'unmarked'}: ${paragraphId.slice(0, 8)}`,
        source, { paragraphId: paragraphId.slice(0, 8), hasFormFeed });
    },

    /**
     * Log grapheme bounds set for paragraph
     */
    graphemeBoundsSet: (paragraphId: string, start: number, end: number) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.height-index.grapheme-bounds-set',
        `Grapheme bounds set: ${paragraphId.slice(0, 8)} [${start}-${end})`,
        source, { paragraphId: paragraphId.slice(0, 8), start, end });
    },
  },

  // ============================================================================
  // Pagination
  // ============================================================================
  paginate: {
    /**
     * Log pagination started
     */
    start: (documentId: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'wasm.paginate.start', `Pagination started for ${documentId}`, source, { documentId, ...data });
    },

    /**
     * Log pagination completed
     */
    complete: (durationMs: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.performanceWithSource('wasm.paginate.complete', 'Pagination completed', durationMs, source, data);
    },

    /**
     * Log pagination error
     * Uses full stack capture for error-level logs
     */
    fail: (error: Error, data?: Record<string, unknown>) => {
      const source = captureFullStack();
      WeBelieveLogger.logWithSource('error', 'wasm.paginate.fail', error.message, source, {
        ...data,
        errorStack: error.stack,
      });
    },

    /**
     * Log pagination from cache
     */
    cacheHit: (durationMs: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.performanceWithSource('wasm.paginate.cache-hit', 'Pagination from cache', durationMs, source, data);
    },

    /**
     * Log pagination result
     */
    result: (pageCount: number, nextCursor: unknown, telemetry?: Record<string, unknown>, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'wasm.paginate.result', `Pagination result: ${pageCount} pages`, source, {
        pageCount,
        nextCursor: nextCursor ?? null,
        telemetry,
        ...data
      });
    },

    /**
     * Log page boundary step
     */
    stepPageBoundary: (pageIndex: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.paginate.step.page-boundary', `Page boundary found at page ${pageIndex}`, source, { pageIndex, ...data });
    },

    /**
     * Log page hashes stored
     */
    hashesStored: (storedCount: number, totalCount: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.paginate.hashes-stored', `Stored ${storedCount} page hashes (total: ${totalCount})`, source, {
        storedCount,
        totalCount,
        ...data
      });
    },

    /**
     * Log paragraph-to-page map built
     */
    paragraphMapBuilt: (totalParagraphs: number, sampleMapping: Array<[string, number[]]>, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.paginate.paragraph-map-built', `Built paragraph-to-page map: ${totalParagraphs} paragraphs`, source, {
        totalParagraphs,
        sampleMapping: sampleMapping.slice(0, 5).map(([paraId, pages]) => ({ paraId: paraId?.slice(0, 8), pages })),
        ...data
      });
    },
  },

  // ============================================================================
  // Snapshot Cache
  // ============================================================================
  snapshot: {
    /**
     * Log snapshot stored
     */
    store: (message: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.snapshot.store', message, source, data);
    },

    /**
     * Log snapshot evicted
     */
    evict: (message: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.snapshot.evict', message, source, data);
    },

    /**
     * Log snapshot preserved
     */
    preserve: (message: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.snapshot.preserve', message, source, data);
    },

    /**
     * Log cache retrieval operation
     */
    cacheGet: (phase: 'call' | 'pre-sanitize' | 'sanitize' | 'post-sanitize', data: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.snapshot.cache-get', `getCachedParagraphSnapshots ${phase}`, source, { phase, ...data });
    },

    /**
     * Log stale snapshots filtered (diagnostic)
     */
    diagStaleFiltered: (count: number, committedLen: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource(
        count > 0 ? 'warn' : 'debug',
        'wasm.snapshot.diag.stale-filtered',
        `Sanitization: ${count} stale lines filtered (committedLen=${committedLen})`,
        source,
        { staleCount: count, committedLen, ...data }
      );
    },

    /**
     * Log snapshot cache check
     */
    cacheCheck: (data: {
      cacheLength: number;
      hasMultipleSnapshots: boolean;
      hasSingleNonPara0: boolean;
      hasProperPerParagraphSnapshots: boolean;
      snapshotIds: string[];
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.snapshot.cache-check', 'Snapshot cache check', source, {
        cacheLength: data.cacheLength,
        hasMultipleSnapshots: data.hasMultipleSnapshots,
        hasSingleNonPara0: data.hasSingleNonPara0,
        hasProperPerParagraphSnapshots: data.hasProperPerParagraphSnapshots,
        snapshotIds: data.snapshotIds.slice(0, 5).map(id => id?.slice(0, 8)),
      });
    },

    /**
     * Log initial paragraph snapshot cached
     */
    initialCached: (linesCount: number, hasCaretMap: boolean, snapshotId: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.snapshot.initial-cached', 'Cached initial paragraph snapshot from full pagination', source, {
        linesCount,
        hasCaretMap,
        snapshotId: snapshotId?.slice(0, 8),
        ...data
      });
    },

    /**
     * Log snapshot merge operation
     */
    merge: (message: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.snapshot.merge', message, source, data);
    },

    /**
     * Log snapshot result
     */
    result: (message: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.snapshot.result', message, source, data);
    },

    /**
     * Log stale snapshots filtered (legacy alias for diagStaleFiltered)
     */
    staleFiltered: (count: number, committedLen: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource(
        count > 0 ? 'warn' : 'debug',
        'wasm.snapshot.diag.stale-filtered',
        `Sanitization: ${count} stale lines filtered (committedLen=${committedLen})`,
        source,
        { staleCount: count, committedLen, ...data }
      );
    },
  },

  // ============================================================================
  // Incremental Text Changes
  // ============================================================================
  incremental: {
    /**
     * Log incremental change started
     */
    start: (oldLen: number, newLen: number, changeStart: number, changeEnd: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.incremental.start', 'Incremental text change', source, {
        oldLen,
        newLen,
        changeStart,
        changeEnd,
        ...data
      });
    },

    /**
     * Log incremental change completed
     */
    complete: (durationMs: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.performanceWithSource('wasm.incremental.complete', 'Incremental update completed', durationMs, source, data);
    },

    /**
     * Log edit delta applied
     */
    deltaApply: (position: number, deleteCount: number, insertText: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.incremental.delta-apply', 'Edit delta', source, {
        position,
        deleteCount,
        insertText: insertText.length > 50 ? insertText.slice(0, 50) + '...' : insertText,
        ...data
      });
    },

    /**
     * Log delta applied result
     */
    deltaApplied: (affectedParagraphs: number, boundariesChanged: boolean, newVersion: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.incremental.delta-applied', 'Delta applied', source, {
        affectedParagraphs,
        boundariesChanged,
        newVersion,
        ...data
      });
    },

    /**
     * Log paragraphs affected count
     */
    paragraphsAffected: (paragraphIds: string[], data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.incremental.paragraphs-affected', 'Calling layoutAffectedParagraphs', source, {
        paragraphIds: paragraphIds.map(id => id.slice(0, 8)),
        count: paragraphIds.length,
        ...data
      });
    },

    /**
     * Log no paragraphs affected (skip)
     */
    noParagraphsAffected: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.incremental.skip', 'No paragraphs affected - skipping layout', source, data);
    },

    /**
     * Log layout completed for incremental
     */
    layoutComplete: (layoutsCount: number, hasErrors: boolean, unknownParagraphsCount: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.incremental.layout-complete', 'Layout completed', source, {
        layouts: layoutsCount,
        hasErrors,
        unknownParagraphs: unknownParagraphsCount,
        ...data
      });
    },

    /**
     * Log waiting for initialization
     */
    waitingForInit: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.incremental.waiting-for-init', 'Waiting for initializeDocument to complete before applying delta', source, data);
    },

    /**
     * Log initialization complete
     */
    initComplete: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.incremental.init-complete', 'initializeDocument complete, proceeding with delta', source, data);
    },
  },

  // ============================================================================
  // Delta/Edit Diagnostics (Paragraph Boundary Debugging)
  // ============================================================================
  delta: {
    /**
     * Log boundaries_changed decision from WASM
     * Critical for debugging overlapping paragraph issues
     */
    boundariesChanged: (data: {
      boundariesChanged: boolean;
      hasDoubleNewline: boolean;
      hasFormFeed: boolean;
      deletedHadDoubleNewline: boolean;
      deletedHadFormFeed: boolean;
      deletingNewlineNearBoundary: boolean;
      editPosition: number;
      deleteCount: number;
      insertLength: number;
      insertText?: string;
    }) => {
      const source = captureCallerSource();
      const level = data.boundariesChanged ? 'info' : 'debug';
      WeBelieveLogger.logWithSource(level, 'wasm.delta.diag.boundaries-changed',
        `boundaries_changed=${data.boundariesChanged} (pos=${data.editPosition}, del=${data.deleteCount}, ins=${data.insertLength})`,
        source, data);
    },

    /**
     * Log paragraph state before and after edit
     * Shows how paragraphs are being split/merged
     */
    paragraphsState: (data: {
      phase: 'before' | 'after' | 'detected';
      paragraphs: Array<{
        id: string;
        graphemeStart: number;
        graphemeEnd: number;
        version?: number;
      }>;
      editPosition?: number;
      boundariesChanged?: boolean;
    }) => {
      const source = captureCallerSource();
      const parasSummary = data.paragraphs.map(p =>
        `${p.id.substring(0, 8)}[${p.graphemeStart}-${p.graphemeEnd})`
      ).join(', ');
      WeBelieveLogger.logWithSource('debug', `wasm.delta.diag.paragraphs-${data.phase}`,
        `Paragraphs ${data.phase}: ${data.paragraphs.length} [${parasSummary}]`,
        source, {
          ...data,
          paragraphs: data.paragraphs.map(p => ({
            id: p.id.substring(0, 8),
            range: `[${p.graphemeStart}-${p.graphemeEnd})`,
            version: p.version
          }))
        });
    },

    /**
     * Log paragraph ID preservation during split/merge
     * Tracks how IDs are matched between old and new paragraphs
     */
    idPreservation: (data: {
      oldParagraphs: Array<{ id: string; graphemeStart: number; graphemeEnd: number }>;
      newParagraphs: Array<{ id: string; graphemeStart: number; graphemeEnd: number }>;
      matchedIds: string[];
      newIds: string[];
      editPosition: number;
    }) => {
      const source = captureCallerSource();
      const hasNewIds = data.newIds.length > 0;
      const level = hasNewIds ? 'info' : 'debug';
      WeBelieveLogger.logWithSource(level, 'wasm.delta.diag.id-preservation',
        `ID preservation: ${data.matchedIds.length} matched, ${data.newIds.length} new`,
        source, {
          oldCount: data.oldParagraphs.length,
          newCount: data.newParagraphs.length,
          matchedIds: data.matchedIds.map(id => id.substring(0, 8)),
          newIds: data.newIds.map(id => id.substring(0, 8)),
          editPosition: data.editPosition,
          oldParagraphs: data.oldParagraphs.map(p => ({
            id: p.id.substring(0, 8),
            range: `[${p.graphemeStart}-${p.graphemeEnd})`
          })),
          newParagraphs: data.newParagraphs.map(p => ({
            id: p.id.substring(0, 8),
            range: `[${p.graphemeStart}-${p.graphemeEnd})`
          }))
        });
    },

    /**
     * Log overlap detection between paragraphs
     * Critical for identifying the root cause of overlapping line errors
     */
    overlapDetected: (data: {
      paragraph1: { id: string; graphemeStart: number; graphemeEnd: number };
      paragraph2: { id: string; graphemeStart: number; graphemeEnd: number };
      overlapRange: { start: number; end: number };
      editContext?: {
        position: number;
        deleteCount: number;
        insertLength: number;
      };
    }) => {
      const source = captureFullStack(); // Full stack for errors
      WeBelieveLogger.logWithSource('error', 'wasm.delta.diag.overlap-detected',
        `OVERLAP: ${data.paragraph1.id.substring(0, 8)}[${data.paragraph1.graphemeStart}-${data.paragraph1.graphemeEnd}) ∩ ${data.paragraph2.id.substring(0, 8)}[${data.paragraph2.graphemeStart}-${data.paragraph2.graphemeEnd})`,
        source, {
          paragraph1: {
            id: data.paragraph1.id.substring(0, 8),
            range: `[${data.paragraph1.graphemeStart}-${data.paragraph1.graphemeEnd})`
          },
          paragraph2: {
            id: data.paragraph2.id.substring(0, 8),
            range: `[${data.paragraph2.graphemeStart}-${data.paragraph2.graphemeEnd})`
          },
          overlapRange: `[${data.overlapRange.start}-${data.overlapRange.end})`,
          editContext: data.editContext
        });
    },

    /**
     * Log affected paragraphs returned from WASM
     */
    affectedParagraphs: (data: {
      affectedIds: string[];
      deletedIds: string[];
      boundariesChanged: boolean;
      newVersion: number;
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.delta.diag.affected',
        `Affected: ${data.affectedIds.length} paragraphs, ${data.deletedIds.length} deleted, boundaries=${data.boundariesChanged}`,
        source, {
          affectedIds: data.affectedIds.map(id => id.substring(0, 8)),
          deletedIds: data.deletedIds.map(id => id.substring(0, 8)),
          boundariesChanged: data.boundariesChanged,
          newVersion: data.newVersion
        });
    },

    /**
     * 🔬 NEW (2026-01-19): Log all paragraph IDs returned by WASM
     * Helps diagnose why WASM returns duplicate paragraphs with same grapheme ranges
     */
    allParagraphIds: (data: {
      allIds: string[];
      affectedIds: string[];
      snapshotIds: string[];
      phase: 'before_layout' | 'after_layout' | 'after_merge';
    }) => {
      const source = captureCallerSource();
      const duplicateCheck = new Set<string>();
      const duplicates = data.allIds.filter(id => {
        if (duplicateCheck.has(id)) return true;
        duplicateCheck.add(id);
        return false;
      });
      const hasDuplicates = duplicates.length > 0;
      const level = hasDuplicates ? 'warn' : 'debug';
      WeBelieveLogger.logWithSource(level, 'wasm.delta.diag.all-paragraph-ids',
        `All paragraph IDs (${data.phase}): ${data.allIds.length} total${hasDuplicates ? `, ${duplicates.length} DUPLICATES` : ''}`,
        source, {
          phase: data.phase,
          allIds: data.allIds.map(id => id?.substring(0, 8)),
          affectedIds: data.affectedIds.map(id => id?.substring(0, 8)),
          snapshotIds: data.snapshotIds.map(id => id?.substring(0, 8)),
          duplicates: duplicates.map(id => id?.substring(0, 8)),
          hasDuplicates
        });
    },

    /**
     * 🔬 NEW (2026-01-19): Log when multiple paragraphs have overlapping grapheme ranges
     * This is a critical diagnostic for the duplicate paragraph bug
     */
    paragraphRangeOverlap: (data: {
      paragraph1: { id: string; snapshotId?: string; graphemeStart: number; graphemeEnd: number; linesCount: number };
      paragraph2: { id: string; snapshotId?: string; graphemeStart: number; graphemeEnd: number; linesCount: number };
      overlapType: 'exact_match' | 'partial_overlap' | 'containment';
      context: string;
    }) => {
      const source = captureFullStack();
      WeBelieveLogger.logWithSource('error', 'wasm.delta.diag.paragraph-range-overlap',
        `PARAGRAPH OVERLAP (${data.overlapType}): ${data.paragraph1.id.substring(0, 8)}[${data.paragraph1.graphemeStart}-${data.paragraph1.graphemeEnd}) vs ${data.paragraph2.id.substring(0, 8)}[${data.paragraph2.graphemeStart}-${data.paragraph2.graphemeEnd})`,
        source, {
          paragraph1: {
            id: data.paragraph1.id.substring(0, 8),
            snapshotId: data.paragraph1.snapshotId?.substring(0, 8),
            range: `[${data.paragraph1.graphemeStart}-${data.paragraph1.graphemeEnd})`,
            linesCount: data.paragraph1.linesCount
          },
          paragraph2: {
            id: data.paragraph2.id.substring(0, 8),
            snapshotId: data.paragraph2.snapshotId?.substring(0, 8),
            range: `[${data.paragraph2.graphemeStart}-${data.paragraph2.graphemeEnd})`,
            linesCount: data.paragraph2.linesCount
          },
          overlapType: data.overlapType,
          context: data.context
        });
    },
  },

  // ============================================================================
  // Delete Diagnostics
  // ============================================================================
  delete: {
    diag: {
      /**
       * Log pre-delete state diagnostic
       */
      before: (data: {
        position: number;
        deleteCount: number;
        deleteEndUtf16: number;
        charBeingDeleted: string;
        charCodeBeingDeleted: number;
        charTypeDeleted: string;
        oldTextLength: number;
        textAroundDelete: string;
        formFeedsBefore: number;
        willDeleteFormFeed: boolean;
        willResultInLength: number;
        timestamp: number;
      }) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'wasm.delete.diag.before', 'About to delete', source, data);
      },

      /**
       * Log post-delete state diagnostic
       */
      after: (data: {
        newTextLength: number;
        charsActuallyDeleted: number;
        formFeedsAfter: number;
        textAroundDelete: string;
        timestamp: number;
      }) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('debug', 'wasm.delete.diag.after', 'After delete', source, data);
      },

      /**
       * Log form-feed removed during delete
       */
      formfeedRemoved: (data?: Record<string, unknown>) => {
        const source = captureCallerSource();
        WeBelieveLogger.logWithSource('warn', 'wasm.delete.diag.formfeed-removed', 'Delete operation removed form-feed', source, data);
      },
    },
  },

  // ============================================================================
  // Memory Diagnostics
  // ============================================================================
  memory: {
    /**
     * Log memory usage diagnostic
     */
    diag: (stats: { heapUsed: number; heapTotal: number }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.memory.diag', 'Memory usage', source, stats);
    },

    /**
     * Log diagnostic events flushed
     */
    flush: (count: number) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.memory.flush', `Flushed ${count} diagnostic events`, source, { count });
    },
  },

  // ============================================================================
  // Document Hash / Content Hash
  // ============================================================================
  contentHash: {
    /**
     * Log content hash changed
     */
    change: (oldHash: string, newHash: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'wasm.content-hash.change', 'Document changed - updating content hash', source, {
        oldHash: oldHash.substring(0, 8),
        newHash: newHash.substring(0, 8),
        ...data
      });
    },

    /**
     * Log saving to cache with explicit hash
     */
    saveWithHash: (hash: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.content-hash.save', `Saving to cache with explicit hash: ${hash.substring(0, 8)}...`, source, {
        hash: hash.substring(0, 8),
        ...data
      });
    },

    /**
     * Log page cache invalidation
     */
    pagesInvalidated: (count: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.content-hash.pages-invalidated', `Invalidated ${count} pages with old docHash`, source, {
        count,
        ...data
      });
    },

    /**
     * Log paragraph snapshot cache cleared
     */
    snapshotCacheCleared: (savedCount: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.content-hash.snapshot-cache-cleared', `Cleared old paragraph snapshot cache (saved ${savedCount} for merge)`, source, {
        savedCount,
        ...data
      });
    },

    /**
     * Log paragraphToPageMap kept
     */
    paragraphMapKept: (entryCount: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.content-hash.paragraph-map-kept', `Kept paragraphToPageMap for SpillFrontier (${entryCount} entries)`, source, {
        entryCount,
        ...data
      });
    },

    /**
     * Log old cache discarded (ID mismatch)
     */
    oldCacheDiscarded: (oldIds: string[], newIds: string[], data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'wasm.content-hash.old-cache-discarded', 'Old cache has different paragraph IDs - discarding (WASM = source of truth)', source, {
        oldIds: oldIds.map(id => id?.substring(0, 8)),
        newIds: newIds.map(id => id?.substring(0, 8)),
        ...data
      });
    },
  },

  // ============================================================================
  // Large Paste Operations
  // ============================================================================
  paste: {
    /**
     * Log large paste detected
     */
    large: (insertionSize: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'wasm.paste.large', `Large paste detected (${insertionSize} chars) - forcing full re-initialization`, source, {
        insertionSize,
        ...data
      });
    },

    /**
     * Log large paste re-initialization complete
     */
    reinitComplete: (newTextLength: number, durationMs: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.performanceWithSource('wasm.paste.reinit-complete', 'Large paste re-initialization complete', durationMs, source, {
        newTextLength,
        ...data
      });
    },
  },

  // ============================================================================
  // Phase 2 Layout Structure (Legacy)
  // ============================================================================
  phase2: {
    /**
     * Log raw layouts structure
     */
    rawLayoutsStructure: (layoutsLength: number, firstLayoutKeys: string[], firstLayoutHasSnapshot: boolean, firstLayoutHasLines: boolean, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.phase2.raw-layouts', 'Raw layouts structure', source, {
        layoutsLength,
        firstLayoutKeys,
        firstLayoutHasSnapshot,
        firstLayoutHasLines,
        ...data
      });
    },

    /**
     * Log extracted snapshots
     */
    extractedSnapshots: (snapshotsCount: number, layoutsCount: number, firstSnapshotKeys?: string[], firstSnapshotLinesCount?: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.phase2.extracted-snapshots', `Extracted ${snapshotsCount} snapshots from ${layoutsCount} layouts`, source, {
        snapshotsCount,
        layoutsCount,
        firstSnapshotKeys,
        firstSnapshotLinesCount,
        ...data
      });
    },
  },

  // ============================================================================
  // Legacy/Compatibility Methods (will be deprecated)
  // These maintain backward compatibility during migration
  // ============================================================================

  /**
   * @deprecated Use specific operation methods instead (e.g., shaping.complete, layout.complete)
   */
  operation: (operation: string, durationMs: number, data?: Record<string, unknown>) => {
    const source = captureCallerSource();
    WeBelieveLogger.performanceWithSource(`wasm.${operation}.complete`, `${operation} completed`, durationMs, source, data);
  },

  /**
   * @deprecated Use snapshot.diagStaleFiltered or specific warning method
   */
  cacheWarning: (message: string, data?: Record<string, unknown>) => {
    const source = captureCallerSource();
    WeBelieveLogger.logWithSource('warn', 'wasm.cache.warn', message, source, data);
  },

  /**
   * @deprecated Use specific .fail methods (e.g., shaping.fail, layout.fail)
   */
  error: (operation: string, error: Error, data?: Record<string, unknown>) => {
    const source = captureCallerSource();
    WeBelieveLogger.logWithSource('error', `wasm.${operation}.fail`, error.message, source, {
      ...data,
      stack: error.stack,
    });
  },

  /**
   * @deprecated Use WasmLogger.init.complete()
   */
  legacyInit: (version: string, data?: Record<string, unknown>) => {
    const source = captureCallerSource();
    WeBelieveLogger.logWithSource('info', 'wasm.init.complete', `WASM initialized: v${version}`, source, { version, ...data });
  },

  /**
   * @deprecated Use specific timing methods
   */
  timing: (operation: string, durationMs: number, data?: Record<string, unknown>) => {
    const source = captureCallerSource();
    WeBelieveLogger.performanceWithSource(`wasm.${operation}.perf`, `${operation}`, durationMs, source, data);
  },

  // Legacy nested objects for backward compatibility
  typesetter: {
    shaping: (durationMs: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.performanceWithSource('wasm.shaping.complete', 'Text shaping completed', durationMs, source, data);
    },
    layout: (durationMs: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.performanceWithSource('wasm.layout.complete', 'Layout completed', durationMs, source, data);
    },
    error: (operation: string, error: Error, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('error', `wasm.typesetter.${operation}.fail`, error.message, source, {
        ...data,
        stack: error.stack,
      });
    },
  },

  diagnostics: {
    memory: (stats: { heapUsed: number; heapTotal: number }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.memory.diag', 'Memory usage', source, stats);
    },
    flush: (count: number) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.memory.flush', `Flushed ${count} diagnostic events`, source, { count });
    },
  },

  pagination: {
    start: (documentId: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'wasm.paginate.start', `Pagination started for ${documentId}`, source, { documentId, ...data });
    },
    complete: (durationMs: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.performanceWithSource('wasm.paginate.complete', 'Pagination completed', durationMs, source, data);
    },
    fromCache: (durationMs: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.performanceWithSource('wasm.paginate.cache-hit', 'Pagination from cache', durationMs, source, data);
    },
    error: (error: Error, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('error', 'wasm.paginate.fail', error.message, source, {
        ...data,
        stack: error.stack,
      });
    },
    result: (pageCount: number, nextCursor: unknown, telemetry?: Record<string, unknown>, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'wasm.paginate.result', `Pagination result: ${pageCount} pages`, source, {
        pageCount,
        nextCursor: nextCursor ?? null,
        telemetry,
        ...data
      });
    },
    hashesStored: (storedCount: number, totalCount: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.paginate.hashes-stored', `Stored ${storedCount} page hashes (total: ${totalCount})`, source, {
        storedCount,
        totalCount,
        ...data
      });
    },
    paragraphMapBuilt: (totalParagraphs: number, sampleMapping: Array<[string, number[]]>, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.paginate.paragraph-map-built', `Built paragraph-to-page map: ${totalParagraphs} paragraphs`, source, {
        totalParagraphs,
        sampleMapping: sampleMapping.slice(0, 5).map(([paraId, pages]) => ({ paraId: paraId?.slice(0, 8), pages })),
        ...data
      });
    },
  },

  phase3a: {
    debug: (message: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.layout.step', message, source, data);
    },
    spillFrontier: (data: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.layout.step.spill-frontier', 'SpillFrontier calculated', source, data);
    },
    paginateStart: (data: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.paginate.step', 'Calling paginateFromCache', source, data);
    },
    paginateComplete: (durationMs: number, data: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.performanceWithSource('wasm.paginate.complete', 'Incremental pagination complete', durationMs, source, data);
    },
  },

  deleteDiag: {
    beforeDelete: (data: {
      position: number;
      deleteCount: number;
      deleteEndUtf16: number;
      charBeingDeleted: string;
      charCodeBeingDeleted: number;
      charTypeDeleted: string;
      oldTextLength: number;
      textAroundDelete: string;
      formFeedsBefore: number;
      willDeleteFormFeed: boolean;
      willResultInLength: number;
      timestamp: number;
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.delete.diag.before', 'About to delete', source, data);
    },
    afterDelete: (data: {
      newTextLength: number;
      charsActuallyDeleted: number;
      formFeedsAfter: number;
      textAroundDelete: string;
      timestamp: number;
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.delete.diag.after', 'After delete', source, data);
    },
  },

  textTracking: {
    lastInitializedTextUpdated: (source_: string, oldLength: number, newLength: number, delta: number, newTail20: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'wasm.text-tracking.updated', 'lastInitializedText updated', source, {
        source: source_,
        oldLength,
        newLength,
        delta,
        newTail20: newTail20.replace(/\n/g, '\\n'),
        ...data
      });
    },
  },

  incrementalChange: {
    start: (oldLen: number, newLen: number, changeStart: number, changeEnd: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.incremental.start', 'Incremental text change', source, {
        oldLen,
        newLen,
        changeStart,
        changeEnd,
        ...data
      });
    },
    editDelta: (position: number, deleteCount: number, insertText: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.incremental.delta-apply', 'Edit delta', source, {
        position,
        deleteCount,
        insertText: insertText.length > 50 ? insertText.slice(0, 50) + '...' : insertText,
        ...data
      });
    },
    deltaApplied: (affectedParagraphs: number, boundariesChanged: boolean, newVersion: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.incremental.delta-applied', 'Delta applied', source, {
        affectedParagraphs,
        boundariesChanged,
        newVersion,
        ...data
      });
    },
    noParagraphsAffected: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.incremental.skip', 'No paragraphs affected - skipping layout', source, data);
    },
    layoutAffectedParagraphs: (paragraphIds: string[], data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.incremental.paragraphs-affected', 'Calling layoutAffectedParagraphs', source, {
        paragraphIds: paragraphIds.map(id => id.slice(0, 8)),
        count: paragraphIds.length,
        ...data
      });
    },
    layoutCompleted: (layoutsCount: number, hasErrors: boolean, unknownParagraphsCount: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.incremental.layout-complete', 'Layout completed', source, {
        layouts: layoutsCount,
        hasErrors,
        unknownParagraphs: unknownParagraphsCount,
        ...data
      });
    },
    waitingForInit: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.incremental.waiting-for-init', 'Waiting for initializeDocument to complete before applying delta', source, data);
    },
    initComplete: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.incremental.init-complete', 'initializeDocument complete, proceeding with delta', source, data);
    },
  },

  docHash: {
    changed: (oldHash: string, newHash: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'wasm.content-hash.change', 'Document changed - updating content hash', source, {
        oldHash: oldHash.substring(0, 8),
        newHash: newHash.substring(0, 8),
        ...data
      });
    },
    saveWithHash: (hash: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.content-hash.save', `Saving to cache with explicit hash: ${hash.substring(0, 8)}...`, source, {
        hash: hash.substring(0, 8),
        ...data
      });
    },
    pagesInvalidated: (count: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.content-hash.pages-invalidated', `Invalidated ${count} pages with old docHash`, source, {
        count,
        ...data
      });
    },
    snapshotCacheCleared: (savedCount: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.content-hash.snapshot-cache-cleared', `Cleared old paragraph snapshot cache (saved ${savedCount} for merge)`, source, {
        savedCount,
        ...data
      });
    },
    paragraphToPageMapKept: (entryCount: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.content-hash.paragraph-map-kept', `Kept paragraphToPageMap for SpillFrontier (${entryCount} entries)`, source, {
        entryCount,
        ...data
      });
    },
    oldCacheDiscarded: (oldIds: string[], newIds: string[], data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'wasm.content-hash.old-cache-discarded', 'Old cache has different paragraph IDs - discarding (WASM = source of truth)', source, {
        oldIds: oldIds.map(id => id?.substring(0, 8)),
        newIds: newIds.map(id => id?.substring(0, 8)),
        ...data
      });
    },
  },

  largePaste: {
    detected: (insertionSize: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'wasm.paste.large', `Large paste detected (${insertionSize} chars) - forcing full re-initialization`, source, {
        insertionSize,
        ...data
      });
    },
    reinitComplete: (newTextLength: number, durationMs: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.performanceWithSource('wasm.paste.reinit-complete', 'Large paste re-initialization complete', durationMs, source, {
        newTextLength,
        ...data
      });
    },
  },

  // ============================================================================
  // Paragraph Formatting (Line Spacing, Font Size, etc.)
  // ============================================================================
  formatting: {
    /**
     * Log formatting update request sent to WASM
     */
    updateRequest: (data: {
      paragraphCount: number;
      paragraphIds: string[];
      lineSpacing?: number;
      fontSizePx?: number;
      fontFamily?: string;
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'wasm.formatting.update-request',
        `Formatting update requested for ${data.paragraphCount} paragraph(s)`,
        source, {
          paragraphCount: data.paragraphCount,
          paragraphIds: data.paragraphIds.map(id => id?.slice(0, 8)),
          lineSpacing: data.lineSpacing,
          fontSizePx: data.fontSizePx,
          fontFamily: data.fontFamily,
        });
    },

    /**
     * Log formatting update result from WASM
     */
    updateResult: (durationMs: number, data: {
      updatedCount: number;
      version: number;
      paragraphIds?: string[];
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.performanceWithSource('wasm.formatting.update-result',
        `Formatting update complete: ${data.updatedCount} paragraph(s) updated`,
        durationMs, source, {
          updatedCount: data.updatedCount,
          version: data.version,
          paragraphIds: data.paragraphIds?.map(id => id?.slice(0, 8)),
        });
    },

    /**
     * Log layout triggered after formatting change
     */
    layoutTriggered: (data: {
      paragraphIds: string[];
      reason: 'line_spacing' | 'font_size' | 'font_family' | 'mixed';
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.formatting.layout-triggered',
        `Layout triggered for ${data.paragraphIds.length} paragraph(s) after ${data.reason} change`,
        source, {
          paragraphIds: data.paragraphIds.map(id => id?.slice(0, 8)),
          reason: data.reason,
        });
    },

    /**
     * Log layout result after formatting change
     */
    layoutResult: (durationMs: number, data: {
      paragraphCount: number;
      layoutsReturned: number;
      snapshotsExtracted: number;
      hasSnapshots: boolean;
    }) => {
      const source = captureCallerSource();
      const level = data.snapshotsExtracted === 0 ? 'warn' : 'debug';
      WeBelieveLogger.performanceWithSource('wasm.formatting.layout-result',
        `Layout after formatting: ${data.layoutsReturned} layouts, ${data.snapshotsExtracted} snapshots`,
        durationMs, source, {
          ...data,
        });
      // Explicit warning if snapshots are missing
      if (data.snapshotsExtracted === 0 && data.layoutsReturned > 0) {
        WeBelieveLogger.logWithSource('warn', 'wasm.formatting.no-snapshots',
          'BREAKAGE: Layouts returned but no snapshots extracted - canvas will not update',
          source, data);
      }
    },

    /**
     * Log cache update after formatting change
     */
    cacheUpdate: (data: {
      snapshotsUpdated: number;
      snapshotsInserted: number;
      totalCached: number;
      updatedIds: string[];
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.formatting.cache-update',
        `Cache updated: ${data.snapshotsUpdated} updated, ${data.snapshotsInserted} inserted`,
        source, {
          snapshotsUpdated: data.snapshotsUpdated,
          snapshotsInserted: data.snapshotsInserted,
          totalCached: data.totalCached,
          updatedIds: data.updatedIds.map(id => id?.slice(0, 8)),
        });
    },

    /**
     * Log cache update skipped (no snapshots to update)
     */
    cacheUpdateSkipped: (data: {
      layoutsCount: number;
      reason: string;
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'wasm.formatting.cache-update-skipped',
        `BREAKAGE: Cache update skipped - ${data.reason}`,
        source, data);
    },

    /**
     * Log pagination after formatting change
     */
    paginationResult: (durationMs: number, data: {
      pagesReturned: number;
      totalPages: number;
      reason: string;
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.performanceWithSource('wasm.formatting.pagination-result',
        `Pagination after formatting: ${data.pagesReturned} pages`,
        durationMs, source, data);
    },

    /**
     * Log complete formatting update flow
     */
    flowComplete: (totalDurationMs: number, data: {
      paragraphsUpdated: number;
      layoutsGenerated: number;
      snapshotsCached: number;
      pagesGenerated: number;
      canvasRedrawTriggered: boolean;
      success: boolean;
    }) => {
      const source = captureCallerSource();
      const level = data.success ? 'info' : 'warn';
      WeBelieveLogger.performanceWithSource('wasm.formatting.flow-complete',
        `Formatting flow ${data.success ? 'complete' : 'INCOMPLETE'}: ${data.paragraphsUpdated} paragraphs → ${data.pagesGenerated} pages`,
        totalDurationMs, source, data);
    },

    /**
     * Log formatting flow failure
     */
    flowFailed: (error: Error, data: {
      stage: 'update' | 'layout' | 'cache' | 'pagination' | 'redraw';
      paragraphIds?: string[];
    }) => {
      const source = captureFullStack();
      WeBelieveLogger.logWithSource('error', 'wasm.formatting.flow-failed',
        `Formatting flow failed at ${data.stage}: ${error.message}`,
        source, {
          stage: data.stage,
          paragraphIds: data.paragraphIds?.map(id => id?.slice(0, 8)),
          errorMessage: error.message,
          errorStack: error.stack,
        });
    },

    // ========================================================================
    // Character-Level Formatting Spans (MS Word-style text runs)
    // ========================================================================

    /**
     * Log span formatting applied to a range
     */
    spanApply: (data: {
      start: number;
      end: number;
      attrs?: string[];
      beforeCount?: number;
      skipped?: boolean;
      reason?: string;
    }) => {
      const source = captureCallerSource();
      if (data.skipped) {
        WeBelieveLogger.logWithSource('debug', 'wasm.formatting.span-apply',
          `Span apply skipped: ${data.reason}`,
          source, data);
      } else {
        WeBelieveLogger.logWithSource('debug', 'wasm.formatting.span-apply',
          `Span apply [${data.start}, ${data.end}): ${data.attrs?.join(', ') || 'none'}`,
          source, data);
      }
    },

    /**
     * Log span merge operation
     */
    spanMerge: (data: {
      beforeCount: number;
      afterCount: number;
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.formatting.span-merge',
        `Spans merged: ${data.beforeCount} → ${data.afterCount}`,
        source, data);
    },

    /**
     * Log selection to paragraph ranges conversion
     */
    selectionToRanges: (data: {
      selectionStart: number;
      selectionEnd: number;
      paragraphsAffected: number;
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.formatting.selection-to-ranges',
        `Selection [${data.selectionStart}, ${data.selectionEnd}) → ${data.paragraphsAffected} paragraph(s)`,
        source, data);
    },

    /**
     * Log formatting applied to selection
     */
    applyFormatting: (data: {
      ranges?: number;
      attrs?: string[];
      skipped?: boolean;
      reason?: string;
    }) => {
      const source = captureCallerSource();
      if (data.skipped) {
        WeBelieveLogger.logWithSource('debug', 'wasm.formatting.apply',
          `Apply formatting skipped: ${data.reason}`,
          source, data);
      } else {
        WeBelieveLogger.logWithSource('info', 'wasm.formatting.apply',
          `Apply formatting to ${data.ranges} range(s): ${data.attrs?.join(', ') || 'none'}`,
          source, data);
      }
    },

    /**
     * Log bold toggle
     */
    toggleBold: (data: {
      isCurrentlyBold: boolean;
      willApply: boolean;
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.formatting.toggle-bold',
        `Toggle bold: currently ${data.isCurrentlyBold ? 'bold' : 'not bold'}, will ${data.willApply ? 'apply' : 'remove'}`,
        source, data);
    },

    /**
     * Log italic toggle
     */
    toggleItalic: (data: {
      isCurrentlyItalic: boolean;
      willApply: boolean;
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'wasm.formatting.toggle-italic',
        `Toggle italic: currently ${data.isCurrentlyItalic ? 'italic' : 'not italic'}, will ${data.willApply ? 'apply' : 'remove'}`,
        source, data);
    },

    /**
     * Log font family change
     */
    setFontFamily: (data: {
      fontFamily: string;
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'wasm.formatting.set-font-family',
        `Set font family: ${data.fontFamily}`,
        source, data);
    },

    /**
     * Log font size change
     */
    setFontSize: (data: {
      fontSizePx: number;
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'wasm.formatting.set-font-size',
        `Set font size: ${data.fontSizePx}px`,
        source, data);
    },

    /**
     * Log formatting removal
     */
    removeFormatting: (data: {
      ranges: number;
      properties: string[];
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'wasm.formatting.remove',
        `Remove formatting from ${data.ranges} range(s): ${data.properties.join(', ')}`,
        source, data);
    },

    /**
     * Log formatting clear (reset to defaults)
     */
    clearFormatting: (data: {
      ranges: number;
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'wasm.formatting.clear',
        `Clear all formatting from ${data.ranges} range(s)`,
        source, data);
    },
  },

  // ============================================================================
  // State Snapshots - AI-Ready Error Context
  // ============================================================================
  stateSnapshot: {
    /**
     * Capture full WASM state snapshot on error
     * Returns a comprehensive snapshot for AI debugging
     *
     * @example
     * try {
     *   await typesetterClient.applyEditDelta(delta);
     * } catch (error) {
     *   const snapshot = WasmLogger.stateSnapshot.captureOnError(error, client, {
     *     operation: 'applyEditDelta',
     *     userAction: 'typing'
     *   });
     * }
     */
    captureOnError: (
      error: Error,
      typesetterClient: any,
      context: {
        operation: string;
        userAction?: string;
        editDelta?: { position: number; deleteCount: number; insertText: string };
        oldTextLength?: number;
        newTextLength?: number;
        affectedParagraphs?: string[];
      }
    ): WasmStateSnapshot => {
      const accessor = createStateAccessor(typesetterClient);
      return WasmStateCapture.captureOnError(error, accessor, context);
    },

    /**
     * Log error with full state snapshot attached
     * This is the recommended way to log WASM errors for AI debugging
     */
    logErrorWithSnapshot: (
      error: Error,
      typesetterClient: any,
      context: {
        operation: string;
        userAction?: string;
        editDelta?: { position: number; deleteCount: number; insertText: string };
        oldTextLength?: number;
        newTextLength?: number;
        affectedParagraphs?: string[];
      }
    ): WasmStateSnapshot => {
      const accessor = createStateAccessor(typesetterClient);
      return WasmStateCapture.logErrorWithSnapshot(error, accessor, context);
    },

    /**
     * Capture lightweight summary for non-critical logging
     */
    captureSummary: (typesetterClient: any, operation: string) => {
      const accessor = createStateAccessor(typesetterClient);
      return WasmStateCapture.captureSummary(accessor, operation);
    }
  }
};

// Re-export types for convenience
export type { TypesetterStateAccessor, WasmStateSnapshot } from './WasmStateSnapshot';
